// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductCompareAndAddToList, {
    ProductCompareAndAddToListStyle,
} from "@steiner/App/Components/Product/ProductCompareAndAddToList";
import ProductListGridContainer, {
    ProductListGridContainerStyle,
} from "@steiner/App/Components/Product/ProductListGridContainer";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import Tooltip, { TooltipPresentationProps } from "@steiner/MobiusOverrides/Tooltip";
import ProductAddToCartButton from "@steiner/Overrides/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@steiner/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@steiner/Overrides/Components/ProductPrice";
import InfoTooltip from "@steiner/XDL/Icons/InfoTooltip";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showVariants?: boolean;
    showPrice?: boolean;
    showAddToCartButton?: boolean;
    showCompare?: boolean;
    showAddToCartActions?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    view: ProductListViewType;
    onAddToCartClick?: () => void;
    containerClassName?: string;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices && state.context.showPricing,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    canAddToCart: canAddToCart(
        state,
        props.productContext.product,
        props.productContext.productInfo,
        props.configurationCompleted,
        props.variantSelectionCompleted,
        props.configurationSelection,
    ),
    hasEnoughInventory: hasEnoughInventory(state, props.productContext),
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productListGridContainer?: ProductListGridContainerStyle;
    priceAndQtyContainer?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    priceContainer?: InjectableCss;
    productFields?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productImageStyles?: ProductListProductImageStyles;
    productAvailableStyles?: ProductAvailabilityStyles;
    messagesContainer?: InjectableCss;
    addToCartActions?: InjectableCss;
    infoAvailabilityWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    qtyDetailsTooltip?: TooltipPresentationProps;
    qtyDetailsTooltipList?: TooltipPresentationProps;
    qtyDetailsTooltipIcon?: IconPresentationProps;
    priceAndAvailabilityContainer?: InjectableCss;
    productCompareAndAddToListStyle?: ProductCompareAndAddToListStyle;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartButton,
    showAddToCartActions, // Show Add to cart + uom + qty
    showCompare = false,
    productContext,
    onAddToCartClick,
    containerClassName,
    canSeePrices,
    view,
    isTabletScreen,
    hasEnoughInventory,
    canAddToCart,
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const { product } = productContext;

    const QtyTooltip = ({ tooltipStyle }: { tooltipStyle?: TooltipPresentationProps }) => {
        const currentUnitOfMeasures = product.unitOfMeasures?.find(
            uom => productContext.productInfo.unitOfMeasure === uom.unitOfMeasure,
        );
        if (!currentUnitOfMeasures || product?.minimumOrderQty <= 1 || !showMinQtyText) {
            return null;
        }

        const message = [product.minimumOrderQty && `${translate("Min Qty")}: ${product.minimumOrderQty}`]
            .filter(Boolean)
            .join(" | ");

        return (
            <Tooltip
                text={message}
                triggerComponent={<Icon {...styles.qtyDetailsTooltipIcon} src={InfoTooltip} />}
                {...tooltipStyle}
            />
        );
    };

    const showMinQtyText = (hasEnoughInventory || productContext.productInfo.failedToLoadInventory) && canAddToCart;

    return (
        <ProductListGridContainer
            extendedStyles={styles.productListGridContainer}
            className={containerClassName}
            data-test-selector="product-price-selector"
        >
            <ProductListGridContainer.Info>
                <StyledWrapper {...styles.priceAndAvailabilityContainer}>
                    {showPrice && canSeePrices && (
                        <StyledWrapper {...styles.priceContainer}>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSalePriceLabel={false}
                                showPack={false}
                                showPricePerUom={true}
                                extendedStyles={styles.productPriceStyles}
                            />
                        </StyledWrapper>
                    )}
                    {showAvailability && <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />}
                </StyledWrapper>
            </ProductListGridContainer.Info>
            <ProductListGridContainer.AddToCartActions>
                {showAddToCartButton && (
                    <ProductAddToCartButton
                        extendedStyles={styles.addToCartButton}
                        onAddToCartClick={onAddToCartClick}
                    />
                )}
                {showAddToCartActions && (
                    <>
                        <StyledWrapper {...styles.addToCartActions}>
                            <StyledWrapper {...styles.productFields}>
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        sizeVariant="small"
                                    />
                                )}
                                <ProductQuantityOrdered />
                                {(view === "Grid" || isTabletScreen) && (
                                    <QtyTooltip tooltipStyle={styles.qtyDetailsTooltipList} />
                                )}
                            </StyledWrapper>

                            <ProductAddToCartButton
                                extendedStyles={styles.addToCartButton}
                                onAddToCartClick={onAddToCartClick}
                                displayAsIcon={view === "Table"}
                            />
                        </StyledWrapper>
                        {((view === "List" && !isTabletScreen) || view === "Table") &&
                            product.minimumOrderQty > 1 &&
                            showMinQtyText && (
                                <StyledWrapper {...styles.messagesContainer} className="row-view">
                                    <Typography size={12}>{translate("Min. qty required")}</Typography>
                                </StyledWrapper>
                            )}
                    </>
                )}
            </ProductListGridContainer.AddToCartActions>
            <ProductListGridContainer.AddToList>
                {(showCompare || showAddToList) && (
                    <ProductCompareAndAddToList
                        showCompare={showCompare}
                        showAddToList={showAddToList}
                        extendedStyles={styles.productCompareAndAddToListStyle}
                    />
                )}
            </ProductListGridContainer.AddToList>
        </ProductListGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
